import { Injectable, inject } from '@angular/core';

import { OfferSource } from '@cds';
import {
    DetailedGrouping,
    DisplayType,
    Game,
    Option,
    OptionMarket,
    OptionMarketStatus,
    OptionStatus,
    Parameter,
    Participant,
    ParticipantPriceType,
    TeamGrouping,
    TotalsPrefix,
    Translation,
    Visibility,
} from '@cds/betting-offer';
import { OfferGroupV2, Tag } from '@cds/betting-offer/grouping/fixture-view';
import { SixPackGroup } from '@cds/betting-offer/grouping/grid-view';
import {
    ComboPrevention,
    ComboPreventionMinimum,
    ComboPreventionType,
    EnhancedFixtureViewGroupingConfiguration,
    EventOption,
    EventOptionGroup,
    EventOptionGrouping,
    EventParticipantImage,
    MapOption,
    ResultSorting,
    SetTab,
    Tv2MarketParameters,
    toResultSorting,
} from '@frontend/sports/betting-offer/feature/model';
import { MarketSubType, MarketType } from '@frontend/sports/betting-offer/feature/types';
import { BetBuilderConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { NonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';

import { sortOptions } from '../helpers/option-sorting';
import { getOptionImage, getParticipantImageMap } from '../helpers/participant-image.helper';
import { CommonFixtureFactory } from './common-fixture.factory';
import { CommonMarketFactory } from './common-market.factory';
import { MarketDisplayFallbackService } from './sports-market-grouping-fallback.service';

export type DetailedFixtureMarket = Game | OptionMarket;

interface MarketFactory {
    create(
        market: DetailedFixtureMarket,
        fixture: string,
        fixtureOnline: boolean,
        grouping: EnhancedFixtureViewGroupingConfiguration,
    ): EventOptionGroup[];

    canCreate(market: DetailedFixtureMarket): boolean;
}

interface RegionalisedGrouping {
    offerGroup: OfferGroupV2;
    marketOrder: number;
    inPlayMarketOrder: number;
    tags: Tag[];
    playerStatsProps: string[];
    teamGrouping: TeamGrouping;
}

abstract class MarketFactoryBase extends CommonMarketFactory {
    constructor(
        protected marketGroupingConfig: MarketGroupingConfig,
        betbuilderConfig: BetBuilderConfig,
    ) {
        super(betbuilderConfig);
    }

    protected getPeriodValue(
        game: Game | OptionMarket,
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        sixPackGroup: SixPackGroup | null = null,
    ): string {
        if (detailedGrouping.displayType === DisplayType.PlayerMilestone || detailedGrouping.displayType === DisplayType.PlayerMilestoneOverUnder) {
            return this.replaceAttrValue(game as OptionMarket, detailedGrouping.displayType);
        }
        if (detailedGrouping.marketTabId !== undefined && grouping.marketTabs) {
            const marketTab = grouping.marketTabs.find((t) => t.id === detailedGrouping.marketTabId);
            if (marketTab) {
                return marketTab.name;
            }
        }
        if (sixPackGroup) {
            return sixPackGroup.name;
        }
        const period = 'parameters' in game ? (game.parameters.find((param) => param.key === Tv2MarketParameters.Period)?.value ?? '') : '';

        return grouping.parameterTranslations?.[`period${period}`] ?? '';
    }

    private replaceAttrValue(game: OptionMarket, displayType: DisplayType): string {
        return game.attr.replace(',', '.') + (displayType === DisplayType.PlayerMilestone ? '+' : '');
    }

    protected getTranslatedValue(translation: Translation): string {
        return translation.short || translation.value;
    }

    protected mapDetailedGrouping(
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        index: number,
        source: OfferSource,
        attrValue?: string,
        forceRegularDisplayType = false,
        isPriceBoosted = false,
        excludePriceboostedMarketGrouping = false,
        parameters: Parameter[] = [],
        sixPackGroup: { groupId: string; sixPack: SixPackGroup } | null = null,
    ): EventOptionGrouping {
        const regionalisedOfferGroup = this.getRegionalisedOfferGroup(detailedGrouping, grouping);
        // isRegular flagged is enabled for few display types, where the mandatory conditions doesn't meet to display in particular display type.
        // If those conditions are not met we are converting these display types to regular.
        const optionGrouping = {
            marketSet: regionalisedOfferGroup ? regionalisedOfferGroup.offerGroup.order : SetTab.Other,
            name:
                grouping.marketGroupsV2[detailedGrouping.marketGroupId]?.marketGroupItems[detailedGrouping.marketGroupItemId]?.name ??
                sixPackGroup?.sixPack.name,
            marketOrder: regionalisedOfferGroup
                ? regionalisedOfferGroup.marketOrder
                : grouping.buildABetSettings?.marketGroupItems?.marketGroupItemPosition,
            inPlayMarketOrder: regionalisedOfferGroup
                ? regionalisedOfferGroup.inPlayMarketOrder
                : grouping.buildABetSettings?.marketGroupItems?.marketGroupItemPosition,
            periodGroup: this.getPeriodGroup(detailedGrouping, grouping, attrValue, parameters, sixPackGroup?.sixPack),
            marketGroup: detailedGrouping.subIndex,
            marketHelpPath: detailedGrouping.marketHelpPath,
            marketTabId: detailedGrouping.marketTabId,
            optionColumnId: detailedGrouping.optionColumnId,
            displayType: detailedGrouping.displayType,
            orderType: detailedGrouping.orderType,
            marketGroupId: detailedGrouping.marketGroupItemId,
            marketGroupItemId: detailedGrouping.marketGroupItemId,
            fixtureParticipantType: detailedGrouping.fixtureParticipantType,
            tags: regionalisedOfferGroup?.tags,
            playerStatsProps: regionalisedOfferGroup?.playerStatsProps,
            teamGrouping: regionalisedOfferGroup?.teamGrouping || undefined,
            sixPackGridId: sixPackGroup?.groupId,
        } satisfies EventOptionGrouping;
        if (forceRegularDisplayType) {
            optionGrouping.marketOrder = isPriceBoosted ? 0 : index;
            optionGrouping.periodGroup = undefined;
            optionGrouping.marketGroup = undefined;
            optionGrouping.displayType = DisplayType.Regular;
            if (isPriceBoosted && !excludePriceboostedMarketGrouping) {
                optionGrouping.marketGroup = Number.MAX_VALUE;
            }
        }

        return optionGrouping;
    }

    private getRegionalisedOfferGroup(
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
    ): RegionalisedGrouping | null {
        const regionalisedOfferGroup = grouping.marketGroupsV2[detailedGrouping.marketGroupId];
        if (!regionalisedOfferGroup) {
            return null;
        }
        const regionalisedMarketGroupItem = regionalisedOfferGroup.marketGroupItems[detailedGrouping.marketGroupItemId];
        if (
            regionalisedMarketGroupItem?.sortId == null &&
            (!this.marketGroupingConfig.useMarketGroupSortingByStage || regionalisedMarketGroupItem?.inPlaySortId == null)
        ) {
            return null;
        }

        if (!this.marketGroupingConfig.regionalisedGroupingEnabled || !detailedGrouping.marketGroupId || !detailedGrouping.marketGroupItemId) {
            return {
                offerGroup: {
                    id: regionalisedOfferGroup.id,
                    order: regionalisedOfferGroup.order,
                    marketGroupItems: {},
                    name: grouping.marketGroupsV2[detailedGrouping.marketGroupId]?.marketGroupItems[detailedGrouping.marketGroupItemId]?.name ?? '',
                    tag: { key: '', value: '' },
                },
                marketOrder: Number.MAX_VALUE,
                inPlayMarketOrder: Number.MAX_VALUE,
                tags: [],
                playerStatsProps: [],
                teamGrouping: TeamGrouping.None,
            };
        }

        return {
            offerGroup: regionalisedOfferGroup,
            marketOrder: regionalisedMarketGroupItem.sortId ?? 0,
            inPlayMarketOrder: regionalisedMarketGroupItem.inPlaySortId ?? 0,
            tags: regionalisedMarketGroupItem?.tags ?? [],
            playerStatsProps: regionalisedMarketGroupItem?.playerStatsProps ?? [],
            teamGrouping: regionalisedMarketGroupItem?.teamGrouping ?? TeamGrouping.None,
        };
    }

    private getPeriodGroup(
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        attrValue?: string,
        parameters?: Parameter[],
        sixPackGroup: SixPackGroup | null = null,
    ): number | undefined {
        if (
            !!attrValue &&
            (detailedGrouping.displayType === DisplayType.PlayerMilestone || detailedGrouping.displayType === DisplayType.PlayerMilestoneOverUnder) &&
            (!this.marketGroupingConfig.enablePlayerMilestoneByMarketSubType ||
                parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value !== MarketType.OverAndUnder ||
                parameters.find((p) => p.key === Tv2MarketParameters.MarketSubType)?.value === MarketSubType.YesNo)
        ) {
            if (isNaN(+attrValue)) {
                return +attrValue.replace(',', '.');
            }
            return +attrValue;
        }
        if (detailedGrouping.marketTabId !== undefined && grouping.marketTabs) {
            const marketTab = grouping.marketTabs.find((t) => t.id === detailedGrouping.marketTabId);
            if (marketTab) {
                return marketTab.sortOrder;
            }
        }
        if (sixPackGroup) {
            return grouping.sixPackGroups[sixPackGroup.id]?.order;
        }

        return undefined;
    }
}

class GameMarketFactory extends MarketFactoryBase implements MarketFactory {
    constructor(
        private marketFallback: MarketDisplayFallbackService,
        marketGroupingConfig: MarketGroupingConfig,
        betbuilderConfig: BetBuilderConfig,
    ) {
        super(marketGroupingConfig, betbuilderConfig);
    }

    canCreate(market: DetailedFixtureMarket): market is Game & boolean {
        return 'visibility' in market;
    }

    create(
        market: DetailedFixtureMarket,
        fixture: string,
        fixtureOnline: boolean,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        longIds?: string[],
        isPartOfSgpGroup?: boolean,
        participants?: Participant[],
    ): EventOptionGroup[] {
        return market.grouping.detailed.map((gameGrouping) =>
            this.createOptionGroup(market, fixture, fixtureOnline, gameGrouping, grouping, longIds, isPartOfSgpGroup, participants),
        );
    }

    //TODO please refactor
    // eslint-disable-next-line max-lines-per-function
    private createOptionGroup(
        market: DetailedFixtureMarket,
        fixture: string,
        fixtureOnline: boolean,
        gameGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        longIds?: string[],
        isPartOfSgpGroup?: boolean,
        participants?: Participant[],
    ): EventOptionGroup {
        const game = market as Game;
        const combo = new ComboPrevention();
        combo.minimum = ComboPreventionMinimum[game.combo2];
        combo.restriction = ComboPreventionType[game.combo1];
        const optionGroup = new EventOptionGroup();
        optionGroup.id = game.id.toString();
        optionGroup.originalId = game.id.toString();
        optionGroup.comboPrevention = combo;
        optionGroup.templateId = game.templateId?.toString() ?? '';
        optionGroup.name = this.getTranslatedValue(game.name);
        optionGroup.visible = game.visibility === Visibility.Visible;
        optionGroup.online = fixtureOnline && optionGroup.visible;
        optionGroup.hidden = game.visibility === Visibility.Hidden;
        optionGroup.detailedGrouping = this.mapDetailedGrouping(
            gameGrouping,
            grouping,
            game.id,
            OfferSource.V1,
            undefined,
            this.marketFallback.shouldForceRegularDisplayTypeTV1(gameGrouping, game),
        );
        optionGroup.periodName = this.getPeriodValue(game, gameGrouping, grouping);
        const categoryId = game.templateCategory && game.templateCategory.id;
        optionGroup.gridGroups = this.getGridGroups(game.grouping, categoryId);
        optionGroup.resultSorting = toResultSorting(game.resultOrder);
        const optionConfig: MapOption = {
            game,
            fixtureId: fixture,
            fixtureOnline,
            resultSorting: optionGroup.resultSorting,
            detailedGrouping: gameGrouping,
            grouping,
            order: gameGrouping.subIndex,
            participants,
        };
        optionGroup.options = this.mapOptions(optionConfig);
        optionGroup.categoryAttribute = game.attr;
        optionGroup.parameterTranslations = grouping.parameterTranslations;
        optionGroup.templateCategoryId = game.categoryId;
        optionGroup.templateCategory = game.category;
        optionGroup.player1 = game.player1 && this.getTranslatedValue(game.player1);
        optionGroup.player2 = game.player2 && this.getTranslatedValue(game.player2);
        optionGroup.isPartOfSgpGroup = !!isPartOfSgpGroup;
        optionGroup.source = OfferSource.V1;
        optionGroup.hasBetBuilderMarkets = !!game.isBetBuilder;
        this.patch(optionGroup, market);

        return optionGroup;
    }

    //TODO please refactor
    // eslint-disable-next-line max-lines-per-function
    private mapOptions(optionConfig: MapOption): EventOption[] {
        const { game, fixtureId, fixtureOnline, resultSorting, detailedGrouping, grouping, order, longIds, participants } = optionConfig;
        let participantImageMap = new Map<number, EventParticipantImage>();

        if (participants) {
            participantImageMap = getParticipantImageMap(participants);
        }

        // eslint-disable-next-line max-lines
        const options = game.results.map((result) => {
            const option = new EventOption();
            option.id = result.id;
            option.name = result.name.short || result.name.value;

            option.nativePrice = NativePrice.fromNativePrice(result);
            option.visible = result.visibility === Visibility.Visible && result.odds > 1 && game.visibility === Visibility.Visible;
            option.online = fixtureOnline && option.visible;
            option.optionGroupId = game.id;
            option.templateId = game.templateId;
            option.originalEventId = fixtureId;
            option.order = order;
            option.playerId = result.playerId;
            option.player1 = game.player1 && this.getTranslatedValue(game.player1);
            option.player1Id = game.player1Id;
            option.player2 = game.player2 && this.getTranslatedValue(game.player2);
            option.player2Id = game.player2Id;
            option.optionColumnId = detailedGrouping.optionColumnId;
            if (detailedGrouping.optionColumnId !== undefined && grouping.optionColumns) {
                const optionColumn = grouping.optionColumns.find((t) => t.id === detailedGrouping.optionColumnId);
                if (optionColumn) {
                    option.optionColumnName = optionColumn.name;
                    option.optionColumnSortOrder = optionColumn.sortOrder;
                }
            }
            option.signature = {
                event: '',
                league: '',
                market: this.getSign(game.name),
                option: this.getSign(result.name),
                region: '',
                sport: '',
            };
            option.overridenName = result.sourceName && result.sourceName.value;
            option.marketAttribute = game.attr;
            option.optionAttribute = result.attr;
            option.totalsPrefix = result.totalsPrefix;
            option.optionSpread = game.spread;
            option.isBetBuilder = !!game.isBetBuilder;
            option.optionOddsKey = game.oddsKey;
            if (longIds?.length) {
                option.longId = longIds.join(';');
            }

            if (participants) {
                option.image = getOptionImage(participants, option, participantImageMap);
            }
            return option;
        });

        return sortOptions(options, resultSorting);
    }

    private getSign(name: Translation): string {
        return (name.short && name.shortSign) || (name.value && name.sign) || '';
    }
}

class OptionMarketFactory extends MarketFactoryBase implements MarketFactory {
    constructor(
        private marketFallback: MarketDisplayFallbackService,
        marketGroupingConfig: MarketGroupingConfig,
        private commonFactory: CommonFixtureFactory,
        betbuilderConfig: BetBuilderConfig,
        private sitecore: Sitecore,
    ) {
        super(marketGroupingConfig, betbuilderConfig);
    }

    canCreate(market: DetailedFixtureMarket): market is OptionMarket & boolean {
        return 'status' in market;
    }

    create(
        market: DetailedFixtureMarket,
        fixture: string,
        fixtureOnline: boolean,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        longIds?: string[],
        isPartOfSgpGroup?: boolean,
        participants?: Participant[],
        isPriceBoosted?: boolean,
        excludePriceboostedMarketGrouping?: boolean,
        forceRegularDisplayType?: boolean,
    ): EventOptionGroup[] {
        return market.grouping.detailed.map((detailedGrouping) =>
            this.createOptionGroup(
                market,
                fixture,
                fixtureOnline,
                detailedGrouping,
                grouping,
                participants,
                isPriceBoosted,
                longIds,
                isPartOfSgpGroup,
                excludePriceboostedMarketGrouping,
                forceRegularDisplayType,
            ),
        );
    }

    //TODO PLEASE REFACTOR BELOW FUNCTION WHEN CHANGING IT
    // eslint-disable-next-line max-lines-per-function
    private createOptionGroup(
        market: DetailedFixtureMarket,
        fixture: string,
        fixtureOnline: boolean,
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        participants: Participant[] | undefined,
        isPriceBoosted?: boolean,
        longIds?: string[],
        isPartOfSgpGroup?: boolean,
        excludePriceboostedMarketGrouping?: boolean,
        forceRegularDisplayType?: boolean,
    ): EventOptionGroup {
        const optionMarket = market as OptionMarket;
        const optionGroup = new EventOptionGroup();
        optionGroup.id = optionMarket.id.toString();
        const combo = new ComboPrevention();
        combo.minimum = optionMarket.minCombo ?? 1;
        combo.restriction = ComboPreventionType[optionMarket.comboPrevention];
        optionGroup.comboPrevention = combo;
        optionGroup.isEachWay = !!optionMarket.isEachWay;
        optionGroup.isStartingPriceAvailable = !!optionMarket.isStartingPriceEnabled;
        optionGroup.isWinPriceAvailable = !!optionMarket.isWinPriceEnabled;
        optionGroup.placeTerms = optionMarket.placeTerms;
        optionGroup.originalId = optionMarket.id.toString();
        optionGroup.templateCategory = optionMarket.templateCategory?.category;
        const categoryId = optionMarket.templateCategory?.id;
        optionGroup.gridGroups = this.getGridGroups(optionMarket.grouping, categoryId);
        const matchedSixPacks = optionMarket.source === OfferSource.V2 ? this.getMatchedSixPacks(optionGroup, grouping, detailedGrouping) : null;
        optionGroup.sixPackGridGroupId = matchedSixPacks?.sixPack.id;
        optionGroup.detailedGrouping = this.mapDetailedGrouping(
            detailedGrouping,
            grouping,
            optionMarket.id,
            OfferSource.V2,
            optionMarket.attr,
            (forceRegularDisplayType && this.marketFallback.shouldForceRegularDisplayTypeTV2(detailedGrouping, optionMarket)) || isPriceBoosted,
            isPriceBoosted,
            excludePriceboostedMarketGrouping,
            optionMarket.parameters,
            matchedSixPacks,
        );
        optionGroup.periodName = this.getPeriodValue(optionMarket, detailedGrouping, grouping, matchedSixPacks?.sixPack);
        optionGroup.name = this.getTranslatedValue(optionMarket.name);
        optionGroup.visible = optionMarket.status === OptionMarketStatus.Visible;
        optionGroup.online = fixtureOnline && optionGroup.visible;
        optionGroup.hidden = optionMarket.status === OptionMarketStatus.Hidden;
        optionGroup.resultSorting = optionMarket.resultOrder ? toResultSorting(optionMarket.resultOrder) : undefined;
        optionGroup.originalParameters = optionMarket.parameters; // ToDo: To be removed after cds obsolete markettype enums are updated in FE.
        optionGroup.player1 = optionMarket.player1 && this.getTranslatedValue(optionMarket.player1);
        optionGroup.player2 = optionMarket.player2 && this.getTranslatedValue(optionMarket.player2);
        optionGroup.source = optionMarket.source;
        optionGroup.options = this.mapOptions(
            optionMarket,
            fixture,
            fixtureOnline,
            detailedGrouping,
            grouping,
            optionGroup,
            detailedGrouping.subIndex,
            isPriceBoosted,
            longIds,
            participants,
        );

        if (optionGroup.resultSorting) {
            optionGroup.options = sortOptions(optionGroup.options, optionGroup.resultSorting);
        }

        if (optionMarket.fixtureParticipantId) {
            optionGroup.fixtureParticipantId = optionMarket.fixtureParticipantId;

            if (participants) {
                const overrideParticipantNameMarkets = this.commonFactory.shouldOverrideParticipantNames(optionMarket, grouping.sportId);
                const playerName = participants.find((x) => x.id === optionMarket.fixtureParticipantId)?.name;
                // when a new market is added, player is not available in push update.Hence removing the options
                if (!playerName && overrideParticipantNameMarkets) {
                    optionGroup.options = [];
                } else {
                    optionGroup.player1 = playerName && this.getTranslatedValue(playerName);
                    optionGroup.options.forEach((opt: EventOption) => {
                        // eslint-disable-next-line no-param-reassign
                        opt.player1 = optionGroup.player1;
                        if (
                            overrideParticipantNameMarkets &&
                            optionGroup.player1 &&
                            detailedGrouping.displayType !== DisplayType.PlayerMilestone &&
                            detailedGrouping.displayType !== DisplayType.PlayerMilestoneOverUnder
                        ) {
                            // eslint-disable-next-line no-param-reassign
                            opt.name = optionGroup.player1;
                        }
                    });
                }
            }
        }
        optionGroup.parameters = optionMarket.parameters;
        optionGroup.categoryAttribute = optionMarket.attr;
        optionGroup.parameterTranslations = grouping.parameterTranslations;
        optionGroup.isPartOfSgpGroup = !!isPartOfSgpGroup;
        optionGroup.hasBetBuilderMarkets = !!optionMarket.isBetBuilder;

        const templateId = optionMarket.parameters.find((f) => f.key === Tv2MarketParameters.TemplateId);
        if (templateId) {
            optionGroup.templateId = templateId.value;
        }

        const templateCategoryId = optionMarket.parameters.find((f) => f.key === Tv2MarketParameters.CategoryId);
        if (templateCategoryId) {
            optionGroup.templateCategoryId = Number(templateCategoryId.value);
        }

        this.patch(optionGroup, market, participants, excludePriceboostedMarketGrouping);
        this.patchDetailedGrouping(optionGroup, market, grouping, participants, this.marketGroupingConfig.correctScoreOptionsSortOrder);

        return optionGroup;
    }

    // eslint-disable-next-line max-lines-per-function
    private mapOptions(
        optionMarket: OptionMarket,
        fixture: string,
        fixtureOnline: boolean,
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        optionGroup: EventOptionGroup,
        order?: number,
        isPriceBoosted?: boolean,
        longIds?: string[],
        participants?: Participant[],
    ): EventOption[] {
        // eslint-disable-next-line no-param-reassign
        optionMarket.options = this.filterOptionsByMarketType(optionMarket, grouping.sportId);

        if (isPriceBoosted) {
            // eslint-disable-next-line no-param-reassign
            optionMarket.options = this.filterOptionsByBoostedPrice(optionMarket);
        }
        const participantImageMap = participants ? getParticipantImageMap(participants) : new Map<number, EventParticipantImage>();
        const hasHandicapParameter = this.hasHandicapParameter(optionMarket, detailedGrouping);

        //TODO please refactor
        // eslint-disable-next-line max-lines-per-function
        return optionMarket.options.map((current, index) => {
            const option = new EventOption();
            option.id = current.id;
            option.name = current.name.short || current.name.value;

            option.nativePrice = NativePrice.fromNativePrice(current.price);
            option.priceId = current.price.id;
            option.boostedPriceId = current.boostedPrice?.id;
            option.boostedPrice = current.boostedPrice && NativePrice.fromNativePrice(current.boostedPrice);
            option.visible =
                current.status === OptionStatus.Visible &&
                (current.participantPriceType === ParticipantPriceType.StartingPrice || option.nativePrice.greaterThan(1)) &&
                optionMarket.status === OptionMarketStatus.Visible;
            option.online = fixtureOnline && option.visible;
            option.optionGroupId = optionMarket.id;
            option.originalEventId = fixture;
            option.order = order;
            option.marketAttribute = optionMarket.attr;
            option.optionAttribute =
                optionMarket.source === OfferSource.V1
                    ? current.attr
                    : this.configureOptionAttribute(current, optionMarket, hasHandicapParameter, detailedGrouping, index);
            option.optionColumnId = detailedGrouping.optionColumnId;
            option.optionSpread = optionMarket.spread;
            option.player1 = optionMarket.player1 && this.getTranslatedValue(optionMarket.player1);
            option.player2 = optionMarket.player2 && this.getTranslatedValue(optionMarket.player2);
            option.player1Id = optionMarket.player1Id;
            option.player2Id = optionMarket.player2Id;
            if (detailedGrouping.optionColumnId !== undefined && grouping.optionColumns) {
                const columnConfig = this.getOptionColumnNameAndSorting(detailedGrouping, grouping, optionGroup);
                option.optionColumnName = columnConfig?.columnName;
                option.optionColumnSortOrder = columnConfig?.sorting;
            }
            option.playerId = current.participantId;
            option.participantId = optionMarket.fixtureParticipantId;
            option.optionPlayerId = current.parameters?.fixtureParticipant;
            option.optionTypes = current.parameters?.optionTypes;
            option.totalsPrefix =
                detailedGrouping.displayType === DisplayType.PlayerCombined
                    ? (current.parameters?.optionTypes[0] as TotalsPrefix)
                    : optionMarket.source === OfferSource.V1
                      ? current.totalsPrefix
                      : undefined;
            option.signature = {
                event: '',
                league: '',
                market: this.getSign(optionMarket.name),
                option: this.getSign(current.name),
                region: '',
                sport: '',
            };
            if (longIds?.length) {
                option.longId = longIds.join(';');
            }
            option.participantPriceType = current.participantPriceType;
            option.priceHistory = current.price.priceHistory;
            option.status = current.status;
            option.isBetBuilder = !!optionMarket.isBetBuilder;
            option.optionOddsKey = optionMarket.oddsKey;

            const templateId = optionMarket.parameters.find((f) => f.key === Tv2MarketParameters.TemplateId);
            if (templateId) {
                option.templateId = Number(templateId.value);
            }

            if (isPriceBoosted) {
                option.optionMarketName = this.getTranslatedValue(optionMarket.name);
            }

            if (participants) {
                option.image = getOptionImage(participants, option, participantImageMap);
            }
            return option;
        });
    }

    private getOptionColumnNameAndSorting(
        detailedGrouping: DetailedGrouping,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        optionGroup: EventOptionGroup,
    ): { columnName: string; sorting: number } | null {
        if (optionGroup.sixPackGridGroupId) {
            const sixPack = grouping.sixPackGroups[optionGroup.sixPackGridGroupId];
            if (!sixPack) {
                return null;
            }
            const index = sixPack.marketGroupIds.findIndex((i) => optionGroup.gridGroups?.includes(i));
            if (index !== -1) {
                return { columnName: sixPack.optionNames[index], sorting: index };
            }
        }
        const optionColumn = grouping.optionColumns.find((t) => t.id === detailedGrouping.optionColumnId);
        if (optionColumn) {
            return { columnName: optionColumn.name, sorting: optionColumn.sortOrder };
        }

        return null;
    }

    private filterOptionsByBoostedPrice(optionMarket: OptionMarket) {
        return optionMarket.options.filter((option) => option.boostedPrice && NativePrice.fromNativePrice(option.boostedPrice).greaterThan(1));
    }

    private filterOptionsByMarketType(optionMarket: OptionMarket, sportId: number): Option[] {
        const marketType = optionMarket.parameters.find((x) => x.key === Tv2MarketParameters.MarketType)?.value;
        const optionTypesToFilter = this.marketGroupingConfig.optionTypesToFilterTV2MarketOptions.find(
            (o) => o.sportId === sportId && o.marketType === marketType,
        )?.optionTypes;
        if (!optionTypesToFilter) {
            return optionMarket.options;
        }

        return optionMarket.options.filter((option) =>
            optionTypesToFilter.some((optionTypeFilters) =>
                optionTypeFilters.every((currentFilter) => option.parameters.optionTypes.includes(currentFilter)),
            ),
        );
    }

    private configureOptionAttribute(
        option: Option,
        optionMarket: OptionMarket,
        hasHandicapParameter: boolean,
        detailedGrouping: DetailedGrouping,
        index: number,
    ): string | undefined {
        switch (detailedGrouping.displayType) {
            case DisplayType.Spread:
            case DisplayType.Head2HeadSixPack:
            case DisplayType.SixPack: {
                const optionName = option.name.value.split(' ')[0];
                if (optionName === TotalsPrefix.Over || optionName === TotalsPrefix.Under) {
                    const prefix =
                        optionName === TotalsPrefix.Over ? this.sitecore.eventGrid.SixPack_TotalsOver : this.sitecore.eventGrid.SixPack_TotalsUnder;
                    return `${prefix} ${optionMarket.attr}`;
                }

                if (hasHandicapParameter) {
                    const optionAttribute = this.extractOptionAttribute(option.name.value);
                    if (optionAttribute) {
                        return optionAttribute;
                    }
                }

                return detailedGrouping.displayType === DisplayType.SixPack || detailedGrouping.displayType === DisplayType.Head2HeadSixPack
                    ? ' '
                    : '0';
            }
            case DisplayType.PlayerMilestone:
            case DisplayType.PlayerMilestoneOverUnder:
                return optionMarket.options[index]?.parameters?.optionTypes[0];
            default:
                return undefined;
        }
    }

    private getSign(name: Translation): string {
        return (name.short && name.shortSign) || (name.value && name.sign) || '';
    }

    private getMatchedSixPacks(
        optionGroup: EventOptionGroup,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        detailedGrouping: DetailedGrouping,
    ): { groupId: string; sixPack: SixPackGroup } | null {
        if (detailedGrouping.displayType !== DisplayType.SixPack) {
            return null;
        }
        return (
            Object.values(grouping.sixPackGroups)
                .filter(isDefined)
                .map((sixPack) => {
                    const matchedGroup = optionGroup.gridGroups?.find((group) => sixPack?.marketGroupIds.includes(group));
                    return matchedGroup ? { groupId: matchedGroup, sixPack } : null;
                })
                .find((result) => result != null) || null
        );
    }

    private hasHandicapParameter(optionMarket: OptionMarket, detailedGrouping: DetailedGrouping): boolean {
        if (
            detailedGrouping.displayType === DisplayType.Spread ||
            detailedGrouping.displayType === DisplayType.SixPack ||
            detailedGrouping.displayType === DisplayType.Head2HeadSixPack
        ) {
            return optionMarket.parameters.some((x) => x.key === Tv2MarketParameters.DecimalHandicap || x.key === Tv2MarketParameters.Handicap);
        }

        return false;
    }
}

@Injectable({ providedIn: 'root' })
export class DetailedFixtureMarketFactory {
    private marketFallback = inject(NonRootToken(MarketDisplayFallbackService)) as MarketDisplayFallbackService;
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private commonFactory = inject(CommonFixtureFactory);
    private betbuilderConfig = inject(BetBuilderConfig);
    private sitecore = inject(Sitecore);

    private factories = [
        new GameMarketFactory(this.marketFallback, this.marketGroupingConfig, this.betbuilderConfig),
        new OptionMarketFactory(this.marketFallback, this.marketGroupingConfig, this.commonFactory, this.betbuilderConfig, this.sitecore),
    ];

    create(
        market: DetailedFixtureMarket,
        fixture: string,
        fixtureOnline: boolean,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        participants?: Participant[],
        isPriceBoosted?: boolean,
        longIds?: string[],
        isPartOfSgpGroup?: boolean,
        excludePriceboostedMarketGrouping?: boolean,
        forceRegularDisplayType?: boolean,
    ): EventOptionGroup[] {
        for (const factory of this.factories) {
            if (factory.canCreate(market)) {
                return factory.create(
                    market,
                    fixture,
                    fixtureOnline,
                    grouping,
                    longIds,
                    isPartOfSgpGroup,
                    participants,
                    isPriceBoosted,
                    excludePriceboostedMarketGrouping,
                    forceRegularDisplayType,
                );
            }
        }
        throw new Error('No market factory found for market');
    }

    update(optionGroup: EventOptionGroup, update: EventOptionGroup, fixtureOnline: boolean, isPriceBoosted?: boolean): EventOptionGroup {
        const updatedGroup = Object.assign(new EventOptionGroup(), optionGroup);
        updatedGroup.comboPrevention = update.comboPrevention;
        updatedGroup.templateId = update.templateId;
        updatedGroup.hidden = update.hidden && !updatedGroup.groupedMarkets?.length;
        updatedGroup.gridGroups = update.gridGroups;
        updatedGroup.resultSorting = update.resultSorting;
        updatedGroup.detailedGrouping.periodGroup = update.detailedGrouping.periodGroup;
        // Update the market order only for betting offer not grouped explicitly for market grouping configuration.
        if (update.detailedGrouping.marketSet === SetTab.Other) {
            updatedGroup.detailedGrouping.marketOrder = update.detailedGrouping.marketOrder;
            updatedGroup.detailedGrouping.inPlayMarketOrder = update.detailedGrouping.inPlayMarketOrder;
        }
        if (isPriceBoosted && update.options.length === 0) {
            updatedGroup.options = [];
        } else {
            this.updateOptions(updatedGroup.options, update.options, fixtureOnline);
            if (isPriceBoosted) {
                updatedGroup.options = this.filterAndUpdatePriceBoostedOptions(updatedGroup.options, updatedGroup.name);
            }
        }
        updatedGroup.options = sortOptions(updatedGroup.options, ResultSorting.Order, optionGroup.resultSorting);
        this.updateGroupMarketIds(updatedGroup, update);
        updatedGroup.online = fixtureOnline && (update.online || updatedGroup.options.some((option) => option.online));
        updatedGroup.player1 = update.player1;
        updatedGroup.player2 = update.player2;

        return updatedGroup;
    }

    private updateOptions(currentOptions: EventOption[], updateOptions: EventOption[], fixtureOnline: boolean): void {
        if (updateOptions.length === 0) {
            return;
        }
        const marketId = updateOptions[0].optionGroupId;
        const currentOptionsList = [...currentOptions];
        currentOptionsList.forEach((current) => {
            if (current.optionGroupId !== marketId) {
                return;
            }
            const currentOptionIndex = currentOptions.findIndex((option) => option.id === current.id);
            const update = updateOptions.find((updateOption) => updateOption.id === current.id);
            if (update) {
                // eslint-disable-next-line no-param-reassign
                currentOptions[currentOptionIndex] = this.createOption(update, fixtureOnline, current);
            } else {
                currentOptions.splice(currentOptionIndex, 1);
            }
        });
        updateOptions
            .filter((update) => !currentOptions.some((current) => current.optionGroupId === update.optionGroupId && current.id === update.id))
            .forEach((create) => {
                currentOptions.push(this.createOption(create, fixtureOnline, undefined));
            });
    }

    private updateGroupMarketIds(optionGroup: EventOptionGroup, update: EventOptionGroup): void {
        if (optionGroup.id !== update.id && optionGroup.groupedMarkets && !optionGroup.groupedMarkets.some((id) => id === update.id.toString())) {
            // eslint-disable-next-line no-param-reassign
            optionGroup.groupedMarkets = optionGroup.groupedMarkets || [];
            optionGroup.groupedMarkets.push(update.id);
        }
    }

    private createOption(update: EventOption | undefined, fixtureOnline: boolean, current: EventOption = new EventOption()): EventOption {
        if (!update) {
            return current;
        }
        // eslint-disable-next-line no-param-reassign
        current.id = update.id;
        // eslint-disable-next-line no-param-reassign
        current.name = update.name;
        // eslint-disable-next-line no-param-reassign
        current.nativePrice = update.nativePrice.clone();
        // eslint-disable-next-line no-param-reassign
        current.priceId = update.priceId;
        // eslint-disable-next-line no-param-reassign
        current.visible = update.visible;
        // eslint-disable-next-line no-param-reassign
        current.online = fixtureOnline && current.visible;
        // eslint-disable-next-line no-param-reassign
        current.optionGroupId = update.optionGroupId;
        // eslint-disable-next-line no-param-reassign
        current.originalEventId = update.originalEventId;
        // eslint-disable-next-line no-param-reassign
        current.order = update.order;
        // eslint-disable-next-line no-param-reassign
        current.optionColumnId = update.optionColumnId;
        // eslint-disable-next-line no-param-reassign
        current.signature = {
            event: '',
            league: '',
            market: update.signature.market,
            option: update.signature.option,
            region: '',
            sport: '',
        };
        // eslint-disable-next-line no-param-reassign
        current.marketAttribute = update.marketAttribute;
        // eslint-disable-next-line no-param-reassign
        current.optionAttribute = update.optionAttribute;
        // eslint-disable-next-line no-param-reassign
        current.optionSpread = update.optionSpread;
        // eslint-disable-next-line no-param-reassign
        current.player1 = update.player1;
        // eslint-disable-next-line no-param-reassign
        current.playerId = update.playerId;
        // eslint-disable-next-line no-param-reassign
        current.optionColumnName = update.optionColumnName;
        // eslint-disable-next-line no-param-reassign
        current.boostedPrice = update.boostedPrice;
        // eslint-disable-next-line no-param-reassign
        current.boostedPriceId = update.boostedPriceId;
        // eslint-disable-next-line no-param-reassign
        current.totalsPrefix = update.totalsPrefix;
        // eslint-disable-next-line no-param-reassign
        current.participantPriceType = update.participantPriceType;
        // eslint-disable-next-line no-param-reassign
        current.priceHistory = update.priceHistory;
        // eslint-disable-next-line no-param-reassign
        current.status = update.status;
        // eslint-disable-next-line no-param-reassign
        current.optionMarketName = update.optionMarketName;
        // eslint-disable-next-line no-param-reassign
        current.optionOddsKey = update.optionOddsKey;

        return current;
    }

    private filterAndUpdatePriceBoostedOptions(options: EventOption[], optionMarketName: string): EventOption[] {
        return options
            .filter((option) => option.boostedPrice?.greaterThan(1))
            .map((option) => {
                // eslint-disable-next-line no-param-reassign
                option.showBoostedPrice = true;
                // eslint-disable-next-line no-param-reassign
                option.optionMarketName = optionMarketName;

                return option;
            });
    }
}
